// Constants
import { getGanttZoomLevelLabel } from "@ms/planner-generic-controls/lib/local/gantt/constants/GanttConstants";
import { getValidatedZoomScale } from "@ms/planner-generic-controls/lib/local/gantt/constants/GanttZoomScaleUtilities";
import { AsyncLoadState } from "@ms/uno-constants/lib/local/AsyncLoadState";
import { EngagementTelemetryConstants } from "@ms/uno-constants/lib/local/EngagementTelemetryConstants";
import { GoalConstants } from "@ms/uno-constants/lib/local/GoalConstants";
import { SupportedPlanTaskGroupingOptions, TaskGroupingOption } from "@ms/uno-constants/lib/local/GroupingConstants";
import { ModalType } from "@ms/uno-constants/lib/local/ModalType";
import { ViewType } from "@ms/uno-constants/lib/local/RoutingConstants";
import { failedStoreEntityStates, StoreEntityState } from "@ms/uno-constants/lib/local/StoreConstants";
import { ViewName } from "@ms/uno-constants/lib/local/ViewConstants";
// Models
import { DefaultPlanCalendarTimeZone } from "@ms/planner-models/lib/local/client/planner/calendar/Calendar";
import { defaultUserTimeSettings } from "@ms/planner-models/lib/local/client/roaming/TimeSettings";
import { ContainerType } from "@ms/planner-models/lib/local/service/planner/enumerations/IPlannerEnumerations";
import { isGroupableCustomFieldDefinition, resolveCustomFieldDefinitionIdFromGroupingOption } from "@ms/planner-models/lib/local/utilities/CustomFieldDefinitionUtilities";
import { useInteractionTracker } from "@ms/planner-presentational-controls/lib/local/hooks/useInteractionTracker";
import { useLoggers } from "@ms/planner-presentational-controls/lib/local/utilities/hooks/useLoggers";
import { ViewActionBar } from "@ms/planner-presentational-controls/lib/local/viewActionBar/ViewActionBar";
import { TraceLevel } from "@ms/uno-telemetry/lib/local/events/Trace.event";
// Hooks
import { useActionCreators } from "@ms/planner-container-controls/lib/local/hooks/useActionCreators";
import { useConfigProvider } from "@ms/planner-container-controls/lib/local/hooks/useConfigProvider";
import { useStores } from "@ms/planner-container-controls/lib/local/hooks/useStores";
// Stores
import { Store } from "@ms/planner-stores/lib/local/stores/Store";
// Action Creators
import { ActionCreator } from "@ms/planner-actioncreators/lib/local/actioncreators/ActionCreator";
// Utilities
import { getPlanPermissionsFromStores } from "@ms/planner-stores/lib/local/utilities/PermissionsUtilities";
import { getSystemTimeZone } from "@ms/uno-utilities/lib/local/DateUtilities-temporal";
import { getTimeZoneDisplayParts } from "@ms/uno-utilities/lib/local/TimeUtilities";
import { getDefaultGroupingOption } from "@ms/uno-utilities/lib/local/ViewUtilities";
import * as React from "react";
const ComponentName = "ViewActionBarContainer";
/**
 * Smart component for plan command bar actions
 */ export const ViewActionBarContainer = ({ planId, onGoToDateSelect, goToDateMinDate, goToDateMaxDate })=>{
    const [routeStore, viewStore, planStore, goalStore, permissionsStore, modernGroupsStore, rosterStore, userStore, teamsChannelStore, appContextStore, driveItemStore, todoStore, customFieldDefinitionStore, dependencyStore, taskStore, customViewStore] = useStores([
        Store.Route,
        Store.View,
        Store.Plan,
        Store.Goal,
        Store.Permissions,
        Store.ModernGroups,
        Store.Roster,
        Store.User,
        Store.TeamsChannel,
        Store.AppContext,
        Store.DriveItem,
        Store.Todo,
        Store.CustomFieldDefinition,
        Store.Dependency,
        Store.Tasks,
        Store.CustomView
    ]);
    const [viewTransformsActionCreator, modalActionCreator, customViewActionCreator, userActionCreator] = useActionCreators([
        ActionCreator.ViewTransforms,
        ActionCreator.Modal,
        ActionCreator.CustomView,
        ActionCreator.User
    ]);
    const { traceLogger, engagementLogger } = useLoggers();
    const { startInteraction } = useInteractionTracker();
    const configProvider = useConfigProvider();
    const handleGoToDateSelect = React.useCallback((date)=>{
        const isTeamView = routeStore.getCurrentViewType() === ViewType.People;
        const telemetryEvent = isTeamView ? EngagementTelemetryConstants.TeamViewGoToDate : EngagementTelemetryConstants.TimelineGoToDate;
        engagementLogger.logEngagement(ComponentName, telemetryEvent);
        try {
            onGoToDateSelect?.(date);
        } catch  {
            traceLogger.logTrace(0x1d851314 /* tag_37rmu */ , TraceLevel.Error, `[${isTeamView ? "Team" : "Timeline"}] Go to date failed [Action=${isTeamView ? "TeamViewGoToDate" : "TimelineGoToDate"}]`);
        }
    }, [
        engagementLogger,
        traceLogger,
        onGoToDateSelect,
        routeStore
    ]);
    const viewId = React.useMemo(()=>({
            name: ViewName.PlanPage,
            partitionKey: planId
        }), [
        planId
    ]);
    const currentPivot = routeStore.getCurrentViewType();
    const flights = configProvider().flights;
    const isGanttHeaderEnabled = currentPivot === ViewType.Timeline && flights.EnableTimelineViewEvo || currentPivot === ViewType.People && flights.EnableTeamViewEvo;
    let zoomSliderProps = undefined;
    if (isGanttHeaderEnabled) {
        const ganttViewType = currentPivot === ViewType.People ? ViewType.People : ViewType.Timeline;
        const zoomScale = getValidatedZoomScale(viewStore.getGanttZoomScale(viewId, ganttViewType));
        zoomSliderProps = {
            value: zoomScale,
            onChange: (scale)=>{
                const telemetryEvent = ganttViewType === ViewType.People ? EngagementTelemetryConstants.TeamViewZoomLevelChanged : EngagementTelemetryConstants.TimelineZoomLevelChanged;
                engagementLogger.logEngagement(ComponentName, telemetryEvent, {
                    previousScale: zoomScale.toString(),
                    newScale: scale.toString()
                });
                viewTransformsActionCreator.setGanttZoomScale(viewId, scale, ganttViewType);
            },
            ariaValueText: getGanttZoomLevelLabel
        };
    }
    // Team View only: Timeline has no unscheduled column to toggle.
    let showUnscheduledToggleProps = undefined;
    if (isGanttHeaderEnabled && currentPivot === ViewType.People) {
        showUnscheduledToggleProps = {
            // Shown unless the user has explicitly hidden it; see getUnscheduledColumnVisibility.
            checked: viewStore.getUnscheduledColumnVisibility(viewId) ?? true,
            onChange: (checked)=>{
                engagementLogger.logEngagement(ComponentName, EngagementTelemetryConstants.TeamViewUnscheduledColumnVisibilityChanged, {
                    isVisible: checked.toString()
                });
                viewTransformsActionCreator.setUnscheduledColumnVisibility(viewId, checked);
            }
        };
    }
    const currentPlan = planStore.getPlan(planId).data ?? null;
    if (!currentPlan) {
        return /*#__PURE__*/ React.createElement(React.Fragment, null);
    }
    if (currentPlan.container.containerType === ContainerType.ToDo) {
        return /*#__PURE__*/ React.createElement(React.Fragment, null);
    }
    let timeZoneMenuProps;
    if (flights.EnableEvoTime && currentPivot !== ViewType.Charts) {
        const selectedTimeZoneDisplay = userStore.getUserSettings().data?.timeSettings?.timeDisplayMode ?? defaultUserTimeSettings.timeDisplayMode;
        const userTimeZone = getSystemTimeZone();
        const planTimeZone = currentPlan.calendar?.timeZone ?? DefaultPlanCalendarTimeZone;
        const now = new Date();
        const userTimeZoneParts = getTimeZoneDisplayParts(userTimeZone, now);
        const planTimeZoneParts = getTimeZoneDisplayParts(planTimeZone, now);
        timeZoneMenuProps = {
            selectedTimeZoneDisplay,
            userTimeZoneLabel: userTimeZoneParts.name,
            userUtcOffset: userTimeZoneParts.utcOffset,
            planTimeZoneLabel: planTimeZoneParts.name,
            planUtcOffset: planTimeZoneParts.utcOffset,
            onTimeZoneDisplayChange: (timeZoneDisplay)=>{
                const latestUserSettings = userStore.getUserSettings().data;
                if (!latestUserSettings) {
                    return;
                }
                const currentTimeSettings = latestUserSettings.timeSettings ?? defaultUserTimeSettings;
                if (currentTimeSettings.timeDisplayMode === timeZoneDisplay) {
                    return;
                }
                const updatedUserSettings = latestUserSettings.setProperty("timeSettings", {
                    ...currentTimeSettings,
                    timeDisplayMode: timeZoneDisplay
                });
                userActionCreator.updateUserSettings(latestUserSettings, updatedUserSettings);
            }
        };
    }
    let groupByDropdownProps = undefined;
    if (currentPivot === ViewType.Board) {
        const pmaAgentEnabledForPlan = getPlanPermissionsFromStores(currentPlan, {
            permissionsStore,
            modernGroupsStore,
            rosterStore,
            userStore,
            teamsChannelStore,
            appContextStore,
            planStore,
            driveItemStore,
            todoStore,
            dependencyStore,
            taskStore
        }, traceLogger).canAssignAgents;
        // Filter out ProjectManagerWorkflowState grouping option if PMA agent is not enabled or not in Board view
        const pmaGroupingOptionEnabled = pmaAgentEnabledForPlan && currentPivot === ViewType.Board;
        let currentTaskGrouping = viewStore.getCurrentTaskGroupingState(viewId);
        // Check if Goals grouping option should be enabled (requires both master flag and board view flag)
        const { EnableGoalsInPlan, EnableGoalsInBoardView, EnableCustomFields } = configProvider().flights;
        const isGoalsGroupingEnabled = EnableGoalsInPlan && EnableGoalsInBoardView;
        const customFieldDefinitions = customFieldDefinitionStore.getCustomFieldDefinitionsForPlan(planId).data;
        const { canAccessFinishDate } = permissionsStore.getUserPermissions();
        // Filter out unsupported grouping options based on feature flags
        const filteredGroupingOptions = SupportedPlanTaskGroupingOptions.filter((option)=>{
            switch(option){
                case TaskGroupingOption.ProjectManagerWorkflowState:
                    return pmaGroupingOptionEnabled;
                case TaskGroupingOption.Goal:
                    return isGoalsGroupingEnabled;
                case TaskGroupingOption.FinishDate:
                    return canAccessFinishDate;
                default:
                    return true;
            }
        });
        // Helper to reset grouping to default if the current grouping option is unavailable
        const resetGroupingIfUnavailable = (grouping, isEnabled)=>{
            if (currentTaskGrouping === grouping && !isEnabled) {
                const defaultGroupingOption = getDefaultGroupingOption(viewId);
                currentTaskGrouping = defaultGroupingOption;
            }
        };
        resetGroupingIfUnavailable(TaskGroupingOption.ProjectManagerWorkflowState, pmaGroupingOptionEnabled);
        resetGroupingIfUnavailable(TaskGroupingOption.Goal, isGoalsGroupingEnabled);
        resetGroupingIfUnavailable(TaskGroupingOption.FinishDate, canAccessFinishDate);
        // If the current grouping is not a standard option, check whether it's a valid CFD grouping;
        // if not (or if the flight is off), reset to default
        if (currentTaskGrouping && !filteredGroupingOptions.includes(currentTaskGrouping)) {
            const matchedCustomFieldDefinition = getValidCustomFieldGrouping(currentTaskGrouping, customFieldDefinitions, EnableCustomFields);
            if (matchedCustomFieldDefinition == null) {
                currentTaskGrouping = getDefaultGroupingOption(viewId);
            }
        }
        groupByDropdownProps = {
            taskGroupingOptions: filteredGroupingOptions,
            currentTaskGrouping: currentTaskGrouping,
            customFieldDefinitions: EnableCustomFields ? customFieldDefinitions.filter((def)=>isGroupableCustomFieldDefinition(def.valueType)) : [],
            onGroupByChange: (taskGroupState)=>{
                const isStandardGrouping = filteredGroupingOptions.includes(taskGroupState);
                const matchedCustomFieldDefinition = getValidCustomFieldGrouping(taskGroupState, customFieldDefinitions, EnableCustomFields);
                const resolvedGrouping = isStandardGrouping || matchedCustomFieldDefinition != null ? taskGroupState : getDefaultGroupingOption(viewId);
                let customFieldExtraData = {};
                if (matchedCustomFieldDefinition != null) {
                    customFieldExtraData = {
                        customFieldValueType: matchedCustomFieldDefinition.valueType
                    };
                }
                engagementLogger.logEngagement(ComponentName, EngagementTelemetryConstants.PlanViewGroupByUpdated, {
                    planId,
                    newGrouping: resolvedGrouping,
                    ...customFieldExtraData
                });
                startInteraction({
                    name: "GroupBy",
                    key: resolvedGrouping
                }, viewId, "planBoard");
                viewTransformsActionCreator.setTaskGroupingState(viewId, resolvedGrouping);
            }
        };
    }
    let onAddGoalClick = undefined;
    let hasReachedMaxGoals = false;
    if (currentPivot === ViewType.Goals) {
        const goalStoreReadResult = goalStore.getGoalsForPlan(planId);
        const goals = goalStoreReadResult?.data ?? [];
        hasReachedMaxGoals = goals.length >= GoalConstants.MaxGoals;
        /* Add Goal button - shown when goals exist and create row is not visible */ if (goals.length > 0) {
            onAddGoalClick = ()=>{
                viewTransformsActionCreator.setAddGoalRowVisibility(true);
            };
        }
    }
    // Conditional coloring button — Grid view only, gated behind flight; disabled without license
    const isConditionalColoringEnabled = flights.EnableConditionalColoringEvo;
    let onOpenConditionalColoring;
    let isConditionalColoringReadOnly = false;
    if (isConditionalColoringEnabled && currentPivot === ViewType.Grid) {
        const { canUpdateConditionalColoringRules } = getPlanPermissionsFromStores(currentPlan, {
            permissionsStore,
            modernGroupsStore,
            rosterStore,
            userStore,
            teamsChannelStore,
            appContextStore,
            planStore,
            driveItemStore,
            todoStore,
            dependencyStore,
            taskStore
        }, traceLogger);
        isConditionalColoringReadOnly = !canUpdateConditionalColoringRules;
        onOpenConditionalColoring = ()=>{
            engagementLogger.logEngagement(ComponentName, EngagementTelemetryConstants.ConditionalColoringDialogOpened);
            modalActionCreator.openModal({
                modalType: ModalType.ConditionalColoring,
                planId
            });
        };
    }
    // Custom Views — offered on every plan (all container types) in all plan views except Report.
    let customViewsMenuProps;
    if (flights.EnableCustomViews && currentPivot != null && currentPivot !== ViewType.Report) {
        const customViewsResult = customViewStore.getCustomViewsForPlan(planId);
        // Saved views are listed alphabetically by display name, using a case-insensitive, locale-aware compare.
        const savedCustomViews = [
            ...customViewsResult.data ?? []
        ].sort((left, right)=>left.displayName.localeCompare(right.displayName, undefined, {
                sensitivity: "base"
            }));
        const { isFetching, state } = customViewsResult.status;
        const hasCachedViews = savedCustomViews.length > 0;
        const loadStatus = isFetching && hasCachedViews ? AsyncLoadState.Loaded : state === StoreEntityState.Absent ? AsyncLoadState.Loading : failedStoreEntityStates.includes(state) ? AsyncLoadState.Failed : AsyncLoadState.Loaded;
        customViewsMenuProps = {
            savedViews: savedCustomViews,
            maxCustomViews: configProvider().settings.clientBasicLimits.maxCustomViews,
            loadStatus,
            onSaveCurrentView: ()=>{
                modalActionCreator.openModal({
                    modalType: ModalType.SaveCustomView,
                    planId,
                    initialView: currentPivot
                });
            },
            onResetAllFilters: ()=>customViewActionCreator.resetCustomView(planId),
            onApplyView: (customViewId)=>customViewActionCreator.applyCustomView(planId, customViewId),
            onRenameView: (customViewId)=>{
                const viewToRename = savedCustomViews.find((view)=>view.id === customViewId);
                if (viewToRename == null) {
                    return;
                }
                modalActionCreator.openModal({
                    modalType: ModalType.RenameCustomView,
                    customViewId,
                    originalString: viewToRename.displayName
                });
            },
            onDeleteView: (customViewId)=>{
                modalActionCreator.openModal({
                    modalType: ModalType.DeleteCustomView,
                    customViewId
                });
            }
        };
    }
    return /*#__PURE__*/ React.createElement(ViewActionBar, {
        customViewsMenuProps: customViewsMenuProps,
        timeZoneMenuProps: timeZoneMenuProps,
        groupByDropdownProps: groupByDropdownProps,
        onAddGoalClick: onAddGoalClick,
        isAddGoalButtonDisabled: hasReachedMaxGoals,
        zoomSliderProps: zoomSliderProps,
        onGoToDateSelect: isGanttHeaderEnabled ? handleGoToDateSelect : undefined,
        onOpenConditionalColoring: onOpenConditionalColoring,
        isConditionalColoringReadOnly: isConditionalColoringReadOnly,
        goToDateMinDate: goToDateMinDate,
        goToDateMaxDate: goToDateMaxDate,
        showUnscheduledToggleProps: showUnscheduledToggleProps
    });
};
/**
 * Returns the groupable custom field definition matching the given grouping ID, or null when
 * the grouping is not a valid, groupable custom field definition (or custom fields are disabled).
 * @param groupingId The current grouping option ID to resolve to a custom field definition
 * @param customFieldDefinitions The plan's custom field definitions to match against
 * @param enableCustomFields Whether the custom fields flight is enabled
 * @returns The matching groupable custom field definition, or null if none applies
 */ function getValidCustomFieldGrouping(groupingId, customFieldDefinitions, enableCustomFields) {
    if (!enableCustomFields) {
        return null;
    }
    const cfdId = resolveCustomFieldDefinitionIdFromGroupingOption(groupingId);
    if (cfdId == null) {
        return null;
    }
    return customFieldDefinitions.find((def)=>def.id === cfdId && isGroupableCustomFieldDefinition(def.valueType)) ?? null;
}
